// ─────────────────────────────────────────────────────────────────────────────
// Automation Breakdown Table — per-automation performance, shown inside
// Platform Performance once a specific platform is selected (added 2026-10).
//
// One row per (automation_name, ticket_type). Respects the same
// basis/date-range/ticket-type/source-filter the rest of Platform
// Performance already uses — this is not an independently-filtered view
// (confirmed with the user 2026-10-06).
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip as MuiTooltip,
  Typography,
} from '@mui/material';
import {
  AutomationBasis,
  AutomationBreakdownRow,
  AutomationTicketType,
  SourceFilterType,
  fetchAutomationBreakdown,
  fetchAutomationTicketDrilldown,
} from '../../services/automationInsightsService';
import { FilterState, buildTimeFilterRequestParams } from '../../contexts/AutomationFilterContext';
import TicketDrilldownModal, { DrilldownContext } from '../common/TicketDrilldownModal';

const BREAKDOWN_COL_MAP: Record<string, { backendCol: string; label: string }> = {
  total:          { backendCol: 'vol_created',   label: 'Total Tickets' },
  tori_raised:    { backendCol: 'tori_vol',      label: 'TORI Raised' },
  fulfilled:      { backendCol: 'auto_fulfilled', label: 'Fulfilled' },
  cne_escalated:  { backendCol: 'cne_count',     label: 'CNE Escalated' },
  reopen_count:   { backendCol: 'reopen_count',  label: 'Reopen' },
  human_takeover: { backendCol: 'human_takeover',label: 'Human Takeover' },
};

interface AutomationBreakdownTableProps {
  platform: string;
  ticketType: AutomationTicketType | 'ALL';
  sourceFilter: SourceFilterType;
  basis: AutomationBasis;
  filters: FilterState;
}

// Display-only — the underlying value stays "RITM" everywhere else in the
// app (API contract, SNOW enum, query params). Matches the existing
// TICKET_TYPE_DISPLAY_LABEL convention in PlatformSummaryPanel.tsx.
const TICKET_TYPE_DISPLAY_LABEL: Record<string, string> = {
  ALL: 'ALL',
  INC: 'INC',
  RITM: 'SR',
};

function formatHours(value: number | null): string {
  if (value === null || value === undefined) return 'N/A';
  return `${value.toFixed(1)}h`;
}

function formatPct(value: number | null): string {
  if (value === null || value === undefined) return 'N/A';
  return `${value.toFixed(1)}%`;
}

function healthChip(row: AutomationBreakdownRow) {
  if (!row.health) return <Chip size="small" label="N/A" />;
  return (
    <MuiTooltip
      title={`Fulfillment ${row.health.fulfillment_score ?? '—'} · Speed ${row.health.speed_score ?? '—'} · Satisfaction ${row.health.satisfaction_score ?? '—'}`}
      arrow
    >
      <Chip
        size="small"
        label={`${row.health.grade} · ${row.health.score}`}
        sx={{
          backgroundColor: row.health.color,
          color: '#0b1120',
          fontWeight: 700,
        }}
      />
    </MuiTooltip>
  );
}

export default function AutomationBreakdownTable({
  platform,
  ticketType,
  sourceFilter,
  basis,
  filters,
}: AutomationBreakdownTableProps) {
  const [rows, setRows] = useState<AutomationBreakdownRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drilldownCtx, setDrilldownCtx] = useState<DrilldownContext | null>(null);

  const openDrilldown = (row: AutomationBreakdownRow, colKey: string) => {
    const mapped = BREAKDOWN_COL_MAP[colKey];
    if (!mapped) return;
    const tp = buildTimeFilterRequestParams(filters);
    setDrilldownCtx({
      title: `${row.automation_name} — ${mapped.label}`,
      showAutomationCols: true,
      fetchPage: (page) => fetchAutomationTicketDrilldown({
        platform, column: mapped.backendCol, basis,
        ticketType: ticketType === 'ALL' ? 'ALL' : ticketType,
        sourceFilter,
        timeFilterType: tp.timeFilterType, selectedPeriods: tp.selectedPeriods,
        dateFrom: tp.dateFrom, dateTo: tp.dateTo,
        automationName: row.automation_name, page, pageSize: 200,
      }),
    });
  };

  useEffect(() => {
    if (platform === 'all') return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const timeParams = buildTimeFilterRequestParams(filters);

    fetchAutomationBreakdown({
      platform,
      ticketType,
      sourceFilter,
      timeFilterType: timeParams.timeFilterType,
      selectedPeriods: timeParams.selectedPeriods,
      dateFrom: timeParams.dateFrom,
      dateTo: timeParams.dateTo,
      basis,
    })
      .then((response) => {
        if (cancelled) return;
        setRows(response.rows ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load automation breakdown');
        setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [platform, ticketType, sourceFilter, basis, filters]);

  const showTicketTypeColumn = ticketType === 'ALL';

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => b.total - a.total),
    [rows]
  );

  if (platform === 'all') return null;

  return (
    <Box mt={4}>
      <Typography variant="h6" fontWeight={700} mb={1}>
        Automations on this Platform
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Stack spacing={1}>
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} variant="rounded" height={40} />
          ))}
        </Stack>
      ) : sortedRows.length === 0 ? (
        !error && (
          <Typography variant="body2" color="text.secondary">
            No automations found for this platform and filter selection.
          </Typography>
        )
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Health</TableCell>
                <TableCell>Automation Name</TableCell>
                {showTicketTypeColumn && <TableCell>Ticket Type</TableCell>}
                <TableCell align="right">Total Tickets</TableCell>
                <TableCell align="right">TORI Raised</TableCell>
                <TableCell align="right">Avg Reopens</TableCell>
                <TableCell>Top Failure Reason</TableCell>
                <TableCell align="right">Fulfilled</TableCell>
                <TableCell align="right">CNE Escalated</TableCell>
                <TableCell align="right">Reopen</TableCell>
                <TableCell align="right">Reopen Loop</TableCell>
                <TableCell align="right">Human Takeover</TableCell>
                <TableCell align="right">Manual Only</TableCell>
                <TableCell align="right">Pending / In-Flight</TableCell>
                <TableCell align="right">Avg MTTR</TableCell>
                <TableCell align="right">MTTR P90</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((row) => (
                <TableRow key={`${row.automation_name}-${row.ticket_type}`} hover>
                  <TableCell>{healthChip(row)}</TableCell>
                  <TableCell>{row.automation_name}</TableCell>
                  {showTicketTypeColumn && (
                    <TableCell>{TICKET_TYPE_DISPLAY_LABEL[row.ticket_type] ?? row.ticket_type}</TableCell>
                  )}
                  <TableCell align="right" sx={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => openDrilldown(row, 'total')}>{row.total.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => openDrilldown(row, 'tori_raised')}>{row.tori_raised.toLocaleString()}</TableCell>
                  <TableCell align="right">{row.avg_reopens.toFixed(2)}</TableCell>
                  <TableCell>
                    {row.top_failure_reason ? (
                      <MuiTooltip title={`${row.top_failure_reason_count} ticket(s)`} arrow>
                        <span>{row.top_failure_reason}</span>
                      </MuiTooltip>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => openDrilldown(row, 'fulfilled')}>{row.fulfilled.toLocaleString()} ({formatPct(row.fulfilled_pct)})</TableCell>
                  <TableCell align="right" sx={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => openDrilldown(row, 'cne_escalated')}>{row.cne_escalated.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => openDrilldown(row, 'reopen_count')}>{row.reopen_count.toLocaleString()}</TableCell>
                  <TableCell align="right">{row.loop.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => openDrilldown(row, 'human_takeover')}>{row.human_takeover.toLocaleString()}</TableCell>
                  <TableCell align="right">{row.manual_only.toLocaleString()}</TableCell>
                  <TableCell align="right">{row.pending_open.toLocaleString()}</TableCell>
                  <TableCell align="right">{formatHours(row.avg_mttr)}</TableCell>
                  <TableCell align="right">{formatHours(row.mttr_p90)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <TicketDrilldownModal context={drilldownCtx} onClose={() => setDrilldownCtx(null)} />
    </Box>
  );
}
